import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { FormControl } from '@angular/forms';

import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';

import { MatAutocompleteTrigger, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { CommandRelay } from '../../commands/command-relay.class';

@Component({
	selector: 'multi-editable-filterable-select',
	templateUrl: 'multi-editable-filterable-select.html',
	styleUrls: ['multi-editable-filterable-select.css']
})
export class MultiEditableFilterableSelectComponent {
	// Le composant de saisie, vu comme un FormControl, pour manipulation de son contenu
	filterInput = new FormControl();

	// Le composant de saisie, vu comme un trigger pour la manipulation de la dropdown list
	@ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger }) input!: MatAutocompleteTrigger;

	// Parametrage du composant
	@Input() id!: string;
	@Input() label!: string;
	@Input() placeholder: string = '';

	itemList: any[] = [];
	selections: boolean[] = [];
	userInputs: boolean[] = [];

	@Input() labelGetter!: (item: any) => string;
	
	@Input() additionalStyleForInput: string = '';

	private initCommandRelay = new CommandRelay<void>(
		() => {
			this.itemList = [];
			this.selections = [];
			this.userInputs = [];
		}
	);
	@Input() set initCommand(obs: Observable<void>) {
		this.initCommandRelay.register(obs);
	}

	private assignSuggestionsCommandRelay = new CommandRelay<string[]>(
		(suggestions: string[]) => {
			if (!suggestions || suggestions.length === 0) {
				suggestions = [];
			}
			this.itemList = [...suggestions];
			this.selections = new Array(this.itemList.length).fill(false);
			this.userInputs = new Array(this.itemList.length).fill(false);
		}
	);
	@Input() set suggestionsCommand(obs: Observable<string[]>) {
		this.assignSuggestionsCommandRelay.register(obs);
	}

	private assignExistingValuesCommandRelay = new CommandRelay<string[]>(
		(existingValues: string[]) => {
			if (existingValues && existingValues.length > 0) {
				existingValues.forEach( existingValue => {
					let foundInSuggestions = false;
					for (let i in this.itemList) {
						if (this.itemList[i] === existingValue) {
							this.selections[i] = true;
							foundInSuggestions = true;
							break;
						}
					}
					if (!foundInSuggestions) {
						this.itemList.push(existingValue);
						this.selections.push(true);
						this.userInputs.push(true);
					}
				} );
			}
		}
	);
	@Input() set existingValuesCommand(obs: Observable<string[]>) {
		this.assignExistingValuesCommandRelay.register(obs);
	}

	@Output() selectedItemsChange = new EventEmitter<any[]>();

	// A cause de divers problemes poses par la maniere dont angular reagit a l'ajout / retrait des options, il faut remettre la valeur du select a null a chaque fois
	selectedItem: number | null = null;

	// La liste actuelle des elements filtres de la drop-down
	filteredItems!: Observable<any[]>;

	// Fonction simplifiant l'utilisation du labelGetter dans l'interface
	getLabel: (item: any) => string = (item) => item ? this.labelGetter(item) : '';

	constructor() {
		this.bindList();
	}
	
	onInputFocus() {
		// Lorsque le focus arrive sur le composant, on reinitialise la liste filtree.
		// En effet, le contenu de la liste source peut avoir change meme si sa reference est identique
		this.bindList();
	}

	onInputBlur(event: any) {
		// Quand on quitte la zone filterable : recupérer la valeur et l'insérer dans la liste des valeurs saisies
		const value = event.currentTarget.value;
		if (value !== '' && this.itemList.indexOf(value) === -1) {
			this.itemList.push(value);
			this.selections.push(true);
			this.userInputs.push(true);
			this.emitSelectionChange();			
		}
		// puis proposer une nouvelle saisie
		this.resetInputValue();
	}

	onInputClick() {
		// Contrairement a un <select> la liste ne s'ouvre pas naturellement quand on clique sur le champ apres la selection d'une valeur
		// Il faut donc le faire programativement
		this.ensureDropdownOpen();
	}

	onInputKeyUp(event: any) {
		const key = event.key;
		if (key === 'Enter') {
			// Meme probleme que pour l'evenement click ci-dessus, mais dans le cas de l'utilisation de la touche 'Enter'
			this.ensureDropdownOpen();
		} else if (key === 'Escape') {
			// Si l'utilisateur appuie sur Esc alors que la liste est ouverte, elle se ferme.
			// Cependant, la saisie qu'il a eventuellement fait demeure alors.
			// Il faut donc la vider programmativement a ce moment
			this.resetInputValue();
		}
	}

	onOptionSelected(event: MatAutocompleteSelectedEvent) {
		// Quand un item est selectionne dans la dropdown list, il faut mettre a jour la donnee du modele
		// Aussi, il est necessaire de lancer les evenements relatifs, pour que le parent se rende compte du changement
		const selectedItem = event.option.value;
		if (selectedItem) {
			const index = selectedItem.index;
			this.selections[index] = true;
			this.userInputs[index] = false;
			this.resetInputValue();
			this.emitSelectionChange();
		}
	}

	onDeselectItem(index: number) {
		this.selections[index] = false;
		this.emitSelectionChange();
	}

	onSelectAll() {
		this.selections.fill(true);
		this.emitSelectionChange();
		this.selectedItem = null;
	}

	onDeselectAll() {
		this.selections.fill(false);
		this.emitSelectionChange();
	}

	private emitSelectionChange() {
		const selection = this.itemList //
			.filter(
				(_item: any, index: number) => this.selections[index]
			);
		this.selectedItemsChange.emit(selection);
	}

	// S'assure que le drowndown list est ouverte
	private ensureDropdownOpen() {
		if (!this.input.panelOpen) {
			this.input.openPanel();
		}
	}

	// (Re-)cree une relation automatique entre la valeur du filtre et la liste affichee
	private bindList() {
		this.filteredItems = this.filterInput.valueChanges.pipe(
			startWith(''),
			// Quand on fixe la valeur de l'input de facon programmative, ce pipe est declenche avec l'item en
			// pametre avant que la directive 'displayWith' le transforme en string.
			// Afin d'empecher des erreurs dans la fonction filterList, on realise une preconversion de la valeur du filtre en string.
			map((value: any) => this.toString(value)),
			map((value: string) => this.filterList(value))
		);
	}

	// Verifie si une valeur est de type string, sinon renvoie ''
	private toString(value: any): string {
		if (value === undefined) {
			return '';
		}

		const type = typeof value;
		if (type === "string") {
			return value;
		}

		return '';
	}

	// Renvoie la liste des valeurs de la liste qui correspondent au filtre passe en parametre
	private filterList(filterValue: string): any[] {

		if (!this.itemList) {
			return [];
		}

		// 1- Transformer les elements pour assurer la preservation de leur index durant le filtrage
		let filteredList = this.itemList.map(
			(item: any, index: number) => {
				return { index: index, entity: item };
			}
		);

		// 2- Filtrer les elements deja selectionnes
		filteredList = filteredList.filter(
			(_item: any, index: number) => {
				return !this.userInputs[index];
			}
		);

		// 3- ne pas suggérer les saisis utilisateurs
		filteredList = filteredList.filter(
			(_item: any, index: number) => {
				return !this.selections[index] || _item === '';
			}
		);

		// 4- Filtrer ensuite les elements qui ne correspondent pas au filtrer saisi
		if (filterValue) {
			const filter = this.normalizeValue(filterValue);
			filteredList = filteredList.filter(
				(item) => {
					let label: string | undefined = this.getLabel(item.entity);
					let normalizedLabel: string = this.normalizeValue(label);
					return normalizedLabel.includes(filter);
				}
			);
		}

		return filteredList;
	}

	// Normalise un string pour comparaison sans etre genee par la casse ou certains caracteres speciaux
	private normalizeValue(value: string | undefined): string {
		if (!value) {
			return '';
		}
		return value.toLowerCase().replace(/\s/g, '');
	}

	// Vide le champ de saisie
	private resetInputValue() {
		this.filterInput.setValue('');
	}
	
	onFocusOut(focusEvent: any, index: number) {
		let value = focusEvent['target']['value'];
		if (this.itemList[index] !==  value) {
			// emettre un signal au conteneur parent pour dire si la valeur saisie a changé
			this.itemList[index] = value;
			this.emitSelectionChange();
		}
	}
}